import ExcelJS from 'exceljs'
import { savingsRange, deduplicatedSavings, VOLUME_EXP } from './networkUtils'

const PRIMARY = '1E5045'
const HEADER_BG = '0E4749'
const WHITE = 'FFFFFF'
const LIGHT_GRAY = 'F8FAFB'
const GREEN = '059669'
const ORANGE = 'F97316'

const TYPE_LABEL = {
  negotiation: 'Negotiation',
  sole_source: 'Sole-Source Risk',
  part_family: 'Part Family',
  quick_win: 'Quick Win',
  site_gap: 'Site Price Gap',
}

/**
 * Export the full SourceOptima intelligence pack to XLSX.
 * Sheets: Executive Summary, Opportunities, Part Detail, Suppliers, Site Price Gaps.
 */
export async function exportOpportunitiesXlsx(opps, { parts = [], stats = {}, params = {} } = {}) {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SourceOptima'
  wb.created = new Date()

  // ═══════════════════════════════════════════════════════════
  // Sheet 1: Executive Summary & Settings
  // ═══════════════════════════════════════════════════════════
  const sum = wb.addWorksheet('Executive Summary')
  sum.columns = [{ width: 40 }, { width: 30 }, { width: 50 }]

  // Title block
  const titleRow = sum.addRow(['SourceOptima — Supply Chain Intelligence Report'])
  titleRow.getCell(1).font = { bold: true, size: 16, color: { argb: PRIMARY } }
  sum.mergeCells(1, 1, 1, 3)
  sum.addRow([`Generated: ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`])
  sum.addRow([])

  // KPIs
  const totalSavings = deduplicatedSavings(opps).total
  const savingsR = savingsRange(totalSavings)
  const totalSpend = stats.total_spend || parts.reduce((s, p) => s + (p.total_spend || 0), 0)

  const kpiHeader = sum.addRow(['Key Performance Indicators', '', ''])
  kpiHeader.getCell(1).font = { bold: true, size: 12, color: { argb: WHITE } }
  kpiHeader.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } } })

  const kpis = [
    ['Total Parts Analyzed', stats.total_parts || parts.length],
    ['Total Suppliers', stats.total_suppliers || new Set(parts.flatMap(p => (p.suppliers || []).map(s => s.name))).size],
    ['Total Cohorts (Peer Groups)', stats.total_families || '—'],
    ['Total Annual Spend', totalSpend ? `€${(totalSpend / 1e6).toFixed(2)}M` : '—'],
    ['Opportunities Identified', opps.length],
    ['Estimated Savings (Low)', `€${Math.round(savingsR.lo).toLocaleString()}`],
    ['Estimated Savings (High)', `€${Math.round(savingsR.hi).toLocaleString()}`],
    ['Savings as % of Spend', totalSpend > 0 ? `${((savingsR.lo / totalSpend) * 100).toFixed(1)}% – ${((savingsR.hi / totalSpend) * 100).toFixed(1)}%` : '—'],
  ]
  kpis.forEach(([label, value]) => {
    const row = sum.addRow([label, value])
    row.getCell(1).font = { bold: true }
  })

  // Breakdown by type
  sum.addRow([])
  const brkHeader = sum.addRow(['Opportunities by Type', 'Count', 'Est. Savings (€)'])
  brkHeader.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } } })
  const byType = {}
  opps.forEach(o => {
    if (!byType[o.type]) byType[o.type] = { count: 0, savings: 0 }
    byType[o.type].count++
    byType[o.type].savings += o.estSavings
  })
  Object.entries(byType).forEach(([type, d]) => {
    const r = savingsRange(d.savings)
    sum.addRow([TYPE_LABEL[type] || type, d.count, `€${Math.round(r.lo).toLocaleString()} – €${Math.round(r.hi).toLocaleString()}`])
  })

  // Settings explanation
  sum.addRow([])
  sum.addRow([])
  const setHeader = sum.addRow(['Analysis Settings', 'Value', 'What This Means'])
  setHeader.eachCell(c => { c.font = { bold: true, color: { argb: WHITE } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } } })

  const settingsExplained = [
    ['Price Threshold', `${params.priceThreshold || 1.3}x`, `Parts priced above ${Math.round(((params.priceThreshold || 1.3) - 1) * 100)}% of their peer group benchmark are flagged as overpriced.`],
    ['Negotiation Target', `${params.negotiationTarget || 1.1}x`, `The goal is to bring prices down to within ${Math.round(((params.negotiationTarget || 1.1) - 1) * 100)}% of benchmark — a realistic "fair premium" over market.`],
    ['Savings Multiplier', `${((params.savingsMultiplier || 0.65) * 100).toFixed(0)}%`, `Savings estimates are discounted to ${((params.savingsMultiplier || 0.65) * 100).toFixed(0)}% of theoretical max to reflect real-world negotiation outcomes.`],
    ['Min. Spend to Flag', `€${(params.minSize || 2000).toLocaleString()}`, 'Opportunities worth less than this are excluded — not worth the effort to pursue.'],
    ['Min. Cohort Size', `${params.minCohortSize || 5} parts`, 'A peer group must have at least this many priced entries for the benchmark to be statistically reliable.'],
    ['Min. Matching Attributes', `${params.similarMinAttrs || 4} of 5`, 'Parts must share at least this many engineering attributes (method, material, size, tolerance, complexity) to be considered true peers.'],
    ['Max. Cohort Dispersion', `${params.dispersionMax || 2.5}x`, 'If a peer group\'s 75th/25th percentile price ratio exceeds this, the group is too noisy for reliable benchmarks.'],
    ['Admin Cost per Supplier (families)', `€${(params.adminCostPerSupplier || 5000).toLocaleString()}/yr`, 'Used in family consolidation savings: estimated annual overhead of managing each additional supplier.'],
    ['Tooling Amort. per Supplier (families)', `€${(params.toolingAmortPerSupplier || 3000).toLocaleString()}`, 'One-time tooling/setup cost attributed per supplier in consolidation analysis.'],
    ['Dual-Source Spend Threshold', `€${(params.dualSourceThreshold || 150000).toLocaleString()}`, 'Parts above this annual spend should maintain two sources regardless — risk trumps savings.'],
  ]
  settingsExplained.forEach(([setting, value, explanation]) => {
    const row = sum.addRow([setting, value, explanation])
    row.getCell(3).alignment = { wrapText: true }
  })

  // Methodology note
  sum.addRow([])
  const noteRow = sum.addRow(['How to read this report:'])
  noteRow.getCell(1).font = { bold: true, size: 11 }
  const notes = [
    'Price Position (PP) = your actual unit cost ÷ the cohort benchmark. PP of 1.5x means you pay 50% more than comparable parts.',
    'Cohort Benchmark = median price of similar parts (same method, material, size, tolerance, complexity), adjusted for your volume.',
    'Savings estimates reflect achievable reductions after applying the savings multiplier — they are deliberately conservative.',
    'Opportunities are de-duplicated: if the same part appears in multiple opportunities, the savings is counted only once.',
    'This report was generated by SourceOptima AI and reflects data as of the export date.',
  ]
  notes.forEach(n => {
    const row = sum.addRow([`• ${n}`])
    row.getCell(1).alignment = { wrapText: true }
    sum.mergeCells(row.number, 1, row.number, 3)
  })

  // ═══════════════════════════════════════════════════════════
  // Sheet 2: Opportunities (enhanced)
  // ═══════════════════════════════════════════════════════════
  const ws = wb.addWorksheet('Opportunities')
  ws.columns = [
    { header: 'Opportunity', key: 'title', width: 52 },
    { header: 'Type', key: 'type', width: 18 },
    { header: 'Parts', key: 'parts', width: 8 },
    { header: 'Spend Exposed (€)', key: 'exposed', width: 18 },
    { header: 'Savings Low (€)', key: 'lo', width: 16 },
    { header: 'Savings High (€)', key: 'hi', width: 16 },
    { header: 'Confidence', key: 'conf', width: 12 },
    { header: 'Key Metric', key: 'metric', width: 18 },
    { header: 'Recommended Action', key: 'action', width: 36 },
    { header: 'Action Score', key: 'score', width: 12 },
  ]
  styleHeader(ws.getRow(1))

  opps.forEach(o => {
    const r = savingsRange(o.estSavings)
    // Derive type-specific metric and action
    let metric = ''
    let action = ''
    let score = ''
    if (o.type === 'negotiation') {
      metric = o.avgPP ? `${o.avgPP.toFixed(2)}x avg PP` : ''
      const topStrategy = (o.strategies || [])[0]
      action = topStrategy ? topStrategy.name : ''
      score = topStrategy ? `${topStrategy.score}/100` : ''
    } else if (o.type === 'part_family') {
      const spread = o.ppRatio || o.vizData?.spread
      metric = spread ? `${Math.round(spread)}× price range` : `${o.vizData?.supplierCount || '?'} suppliers`
      action = o.vizData?.hasConsolidation ? 'Consolidate suppliers' : 'Align to best-in-family'
      score = ''
    } else if (o.type === 'sole_source') {
      metric = `${o.parts.length} sole-sourced`
      action = 'Qualify 2nd source'
      score = ''
    } else if (o.type === 'quick_win') {
      const d = o.vizData || {}
      metric = d.current?.pp ? `${d.current.pp.toFixed(2)}x → ${d.alternative?.pp?.toFixed(2) || '?'}x` : ''
      action = 'Switch to qualified alt.'
      score = ''
    } else if (o.type === 'site_gap') {
      metric = o.vizData?.sites ? `${o.vizData.sites.length} sites` : ''
      action = 'Harmonize cross-site pricing'
      score = ''
    }
    const row = ws.addRow({
      title: o.title,
      type: TYPE_LABEL[o.type] || o.type,
      parts: o.vizData?.partCount ?? o.parts.length,
      exposed: Math.round(o.spendExposed),
      lo: Math.round(r.lo),
      hi: Math.round(r.hi),
      conf: o.confidence,
      metric,
      action,
      score,
    })
    ;['exposed', 'lo', 'hi'].forEach(k => { row.getCell(k).numFmt = '#,##0' })
    row.getCell('lo').font = { color: { argb: GREEN }, bold: true }
    row.getCell('hi').font = { color: { argb: GREEN }, bold: true }
  })
  zebra(ws, 2)

  // ═══════════════════════════════════════════════════════════
  // Sheet 3: Part Detail (full engineering context + benchmark)
  // ═══════════════════════════════════════════════════════════
  const pd = wb.addWorksheet('Part Detail')
  pd.columns = [
    { header: 'Opportunity', key: 'opp', width: 36 },
    { header: 'Type', key: 'type', width: 15 },
    { header: 'Part #', key: 'pn', width: 18 },
    { header: 'Description', key: 'desc', width: 30 },
    { header: 'Supplier', key: 'sup', width: 22 },
    { header: 'Unit Cost (€)', key: 'uc', width: 13 },
    { header: 'Benchmark (€)', key: 'bm', width: 13 },
    { header: 'Target (€)', key: 'target', width: 12 },
    { header: 'Price Pos.', key: 'pp', width: 10 },
    { header: 'Spend (€)', key: 'spend', width: 13 },
    { header: 'EAU', key: 'eau', width: 9 },
    { header: 'Method', key: 'method', width: 14 },
    { header: 'Material', key: 'material', width: 14 },
    { header: 'Size', key: 'size', width: 10 },
    { header: 'Tolerance', key: 'tolerance', width: 12 },
    { header: 'Complexity', key: 'complexity', width: 12 },
    { header: 'Cohort', key: 'cohort', width: 26 },
    { header: 'Cohort Size', key: 'cohortSize', width: 11 },
    { header: 'Sources', key: 'sources', width: 9 },
  ]
  styleHeader(pd.getRow(1))

  const nt = params.negotiationTarget || 1.1
  opps.forEach(o => {
    o.parts.forEach(p => {
      (p.suppliers || []).forEach(s => {
        // Compute target price (same formula as dashboard)
        let target = null
        if (s.benchmark) {
          const rawTarget = s.benchmark * nt
          const peers = (s.cohort_entries || []).filter(e => e.unit_cost > 0)
          const cheapest = peers.length ? peers.reduce((a, b) => (a.unit_cost < b.unit_cost ? a : b)) : null
          const eau = parseFloat(s.eau) || 0
          const peerFloor = cheapest ? cheapest.unit_cost * volAdjust(cheapest.eau || 0, eau) : rawTarget
          target = Math.max(rawTarget, peerFloor)
        }
        const sources = (p.suppliers || []).filter(x => x.unit_cost > 0).length
        const row = pd.addRow({
          opp: o.title.slice(0, 50),
          type: TYPE_LABEL[o.type] || o.type,
          pn: p.pn,
          desc: p.description || '',
          sup: s.name,
          uc: s.unit_cost ? Number(s.unit_cost) : null,
          bm: s.benchmark || null,
          target: target,
          pp: s.price_position != null ? s.price_position : null,
          spend: Math.round(s.spend || 0),
          eau: parseFloat(s.eau) || null,
          method: p.method || '',
          material: p.material || '',
          size: p.size || '',
          tolerance: p.tolerance_band || '',
          complexity: p.complexity_tier || '',
          cohort: s.cohort_name || '',
          cohortSize: s.cohort_size || null,
          sources: sources,
        })
        row.getCell('uc').numFmt = '#,##0.00'
        row.getCell('bm').numFmt = '#,##0.00'
        row.getCell('target').numFmt = '#,##0.00'
        row.getCell('spend').numFmt = '#,##0'
        row.getCell('eau').numFmt = '#,##0'
        if (s.price_position != null) {
          row.getCell('pp').numFmt = '0.00"x"'
          if (s.price_position > 1.5) row.getCell('pp').font = { color: { argb: 'DC2626' }, bold: true }
          else if (s.price_position <= 1.0) row.getCell('pp').font = { color: { argb: GREEN } }
        }
        if (target && s.unit_cost && Number(s.unit_cost) > target) {
          row.getCell('target').font = { color: { argb: GREEN }, bold: true }
        }
      })
    })
  })
  zebra(pd, 2)
  pd.views = [{ state: 'frozen', ySplit: 1 }]

  // ═══════════════════════════════════════════════════════════
  // Sheet 4: Supplier Scorecard
  // ═══════════════════════════════════════════════════════════
  const sc = wb.addWorksheet('Suppliers')
  sc.columns = [
    { header: 'Supplier', key: 'name', width: 28 },
    { header: 'Total Parts', key: 'totalParts', width: 11 },
    { header: 'Total Spend (€)', key: 'spend', width: 16 },
    { header: 'Avg Price Position', key: 'avgPP', width: 16 },
    { header: 'Parts Above Benchmark', key: 'above', width: 20 },
    { header: '% Above Benchmark', key: 'pctAbove', width: 16 },
    { header: 'Sole-Source Parts', key: 'sole', width: 16 },
    { header: 'Opps Involving', key: 'opps', width: 14 },
  ]
  styleHeader(sc.getRow(1))

  // Build supplier map from all parts
  const supplierMap = {}
  ;(parts.length ? parts : opps.flatMap(o => o.parts)).forEach(p => {
    (p.suppliers || []).forEach(s => {
      if (!s.name) return
      if (!supplierMap[s.name]) supplierMap[s.name] = { parts: new Set(), spend: 0, pps: [], above: 0, sole: 0, opps: new Set() }
      const entry = supplierMap[s.name]
      entry.parts.add(p.pn)
      entry.spend += (s.spend || 0)
      if (s.price_position != null && s.price_position > 0) {
        entry.pps.push(s.price_position)
        if (s.price_position > 1.0) entry.above++
      }
      const sources = (p.suppliers || []).filter(x => x.unit_cost > 0).length
      if (sources === 1) entry.sole++
    })
  })
  // Tag which suppliers appear in opportunities
  opps.forEach(o => {
    o.parts.forEach(p => {
      (p.suppliers || []).forEach(s => {
        if (s.name && supplierMap[s.name]) supplierMap[s.name].opps.add(o.title)
      })
    })
  })
  // Sort by spend descending
  Object.entries(supplierMap)
    .sort((a, b) => b[1].spend - a[1].spend)
    .forEach(([name, d]) => {
      const avgPP = d.pps.length > 0 ? d.pps.reduce((a, b) => a + b, 0) / d.pps.length : null
      const row = sc.addRow({
        name,
        totalParts: d.parts.size,
        spend: Math.round(d.spend),
        avgPP,
        above: d.above,
        pctAbove: d.pps.length > 0 ? d.above / d.pps.length : null,
        sole: d.sole,
        opps: d.opps.size,
      })
      row.getCell('spend').numFmt = '#,##0'
      if (avgPP != null) row.getCell('avgPP').numFmt = '0.00"x"'
      if (d.pps.length > 0) row.getCell('pctAbove').numFmt = '0%'
      if (d.opps.size > 0) row.getCell('opps').font = { color: { argb: ORANGE }, bold: true }
    })
  zebra(sc, 2)

  // ═══════════════════════════════════════════════════════════
  // Sheet 5: Site Price Gaps (existing)
  // ═══════════════════════════════════════════════════════════
  const siteOpps = opps.filter(o => o.type === 'site_gap')
  if (siteOpps.length) {
    const sg = wb.addWorksheet('Site Price Gaps')
    sg.columns = [
      { header: 'Part #', key: 'pn', width: 18 },
      { header: 'Description', key: 'desc', width: 28 },
      { header: 'Site', key: 'site', width: 22 },
      { header: 'Supplier(s)', key: 'sup', width: 30 },
      { header: 'Unit Cost (€)', key: 'uc', width: 14 },
      { header: 'Δ vs Best', key: 'delta', width: 12 },
      { header: 'Site Spend (€)', key: 'spend', width: 16 },
    ]
    styleHeader(sg.getRow(1))
    siteOpps.forEach(o => {
      const best = o.vizData.best
      o.vizData.sites.forEach(s => {
        const over = best > 0 ? s.unit_cost / best - 1 : 0
        const row = sg.addRow({
          pn: o.vizData.pn, desc: o.vizData.desc || '', site: s.site, sup: (s.suppliers || []).join(', '),
          uc: Number(s.unit_cost), delta: over, spend: Math.round(s.spend),
        })
        row.getCell('uc').numFmt = '#,##0.00'
        row.getCell('spend').numFmt = '#,##0'
        row.getCell('delta').numFmt = '+0%;-0%'
        if (over <= 0) row.getCell('delta').font = { color: { argb: GREEN }, bold: true }
        else if (over > 0.3) row.getCell('delta').font = { color: { argb: 'DC2626' }, bold: true }
      })
    })
    zebra(sg, 2)
  }

  // ═══════════════════════════════════════════════════════════
  // Sheet 6: All Parts (full catalog with engineering attrs)
  // ═══════════════════════════════════════════════════════════
  if (parts.length) {
    const ap = wb.addWorksheet('All Parts')
    ap.columns = [
      { header: 'Part #', key: 'pn', width: 18 },
      { header: 'Description', key: 'desc', width: 30 },
      { header: 'Method', key: 'method', width: 14 },
      { header: 'Material', key: 'material', width: 14 },
      { header: 'Size', key: 'size', width: 10 },
      { header: 'Tolerance', key: 'tolerance', width: 12 },
      { header: 'Complexity', key: 'complexity', width: 12 },
      { header: 'EAU', key: 'eau', width: 9 },
      { header: 'Suppliers', key: 'suppliers', width: 28 },
      { header: 'Best Price Pos.', key: 'bestPP', width: 13 },
      { header: 'Total Spend (€)', key: 'spend', width: 14 },
      { header: 'Sources', key: 'sources', width: 9 },
      { header: 'In Opportunity?', key: 'inOpp', width: 14 },
    ]
    styleHeader(ap.getRow(1))

    // Build set of part numbers appearing in opportunities
    const oppPNs = new Set()
    opps.forEach(o => o.parts.forEach(p => oppPNs.add(p.pn)))

    parts.forEach(p => {
      const suppliers = (p.suppliers || []).filter(s => s.unit_cost > 0)
      const pps = suppliers.map(s => s.price_position).filter(x => x != null && x > 0)
      const bestPP = pps.length ? Math.min(...pps) : null
      const totalSpend = suppliers.reduce((s, x) => s + (x.spend || 0), 0)
      const row = ap.addRow({
        pn: p.pn,
        desc: p.description || '',
        method: p.method || '',
        material: p.material || '',
        size: p.size || '',
        tolerance: p.tolerance_band || '',
        complexity: p.complexity_tier || '',
        eau: parseFloat(p.eau || p.suppliers?.[0]?.eau) || null,
        suppliers: suppliers.map(s => s.name).join(', '),
        bestPP,
        spend: Math.round(totalSpend),
        sources: suppliers.length,
        inOpp: oppPNs.has(p.pn) ? 'Yes' : '',
      })
      row.getCell('spend').numFmt = '#,##0'
      row.getCell('eau').numFmt = '#,##0'
      if (bestPP != null) {
        row.getCell('bestPP').numFmt = '0.00"x"'
        if (bestPP > 1.3) row.getCell('bestPP').font = { color: { argb: 'DC2626' } }
        else if (bestPP <= 1.0) row.getCell('bestPP').font = { color: { argb: GREEN } }
      }
      if (oppPNs.has(p.pn)) row.getCell('inOpp').font = { color: { argb: ORANGE }, bold: true }
    })
    zebra(ap, 2)
    ap.views = [{ state: 'frozen', ySplit: 1 }]
    ap.autoFilter = { from: { row: 1, column: 1 }, to: { row: ap.rowCount, column: 13 } }
  }

  // ── Download ──
  const buf = await wb.xlsx.writeBuffer()
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `SourceOptima_Intelligence_Report_${new Date().toISOString().slice(0, 10)}.xlsx`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ── Volume adjustment helper (mirrors networkUtils) ──
function volAdjust(fromEAU, toEAU) {
  if (!fromEAU || !toEAU || fromEAU <= 0 || toEAU <= 0) return 1
  return Math.pow(fromEAU / toEAU, VOLUME_EXP)
}

function styleHeader(row) {
  row.eachCell(c => {
    c.font = { bold: true, color: { argb: WHITE }, size: 11 }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
    c.alignment = { vertical: 'middle' }
    c.border = { bottom: { style: 'thin', color: { argb: PRIMARY } } }
  })
  row.height = 20
}

function zebra(ws, from) {
  for (let i = from; i <= ws.rowCount; i++) {
    if (i % 2 === 0) {
      ws.getRow(i).eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LIGHT_GRAY } }
      })
    }
  }
}
